// Inline glossary for the dashboard's most-jargony terms. Entries surface
// via the <GlossaryTerm term="agb">AGB</GlossaryTerm> component as a
// dotted-underline span with a hover/focus/tap tooltip carrying the full
// name + a 1-2 sentence definition + an optional source URL.
//
// Definitions verified 2026-05-03 against authoritative sources (USFS,
// NatureServe, USDA NRS, peer-reviewed papers); see commit history for
// the verification notes. When updating an entry, re-check the source —
// these are the team's public claims at the SFI annual conference.

export interface GlossaryEntry {
  /** Canonical full name as it would appear on first use in formal
   *  copy (e.g. "Above-Ground Biomass", not "Aboveground biomass"). */
  full: string;
  /** 1-2 sentence plain-English definition. ~120-180 chars. Audience:
   *  forester / policy mix at the SFI conference. */
  gloss: string;
  /** Optional canonical source URL. Renders as a "Source →" link in
   *  the tooltip. Skip when no single authoritative page exists. */
  href?: string;
}

// Direct annotation rather than `as const satisfies …` because `as const`
// narrows each entry to its literal shape — entries without `href` then
// lose the optional property at the type level, and consumers reading
// `entry.href` fail to type-check.
export type GlossaryTermId =
  | 'agb'
  | 'rd'
  | 'whp'
  | 'mobi'
  | 'fia'
  | 'dbh'
  | 'mg_ha'
  | 'shannon_index'
  | 'pielou_evenness'
  | 'g1_g2'
  | 'luca'
  | 'nsvb'
  | 'treemap'
  | 'pwrsr'
  | 'sawtimber'
  | 'fia_forest_type';

export const GLOSSARY: Record<GlossaryTermId, GlossaryEntry> = {
  agb: {
    full: 'Above-Ground Biomass',
    gloss:
      "The oven-dry mass of all living plant material above the soil surface — trunks, bark, branches, twigs, and foliage. Usually reported in megagrams per hectare (Mg/ha). Excludes belowground roots.",
    href: 'https://lpvs.gsfc.nasa.gov/AGB/AGB_home.html',
  },
  rd: {
    full: 'Relative Density (RD)',
    gloss:
      "A foresters' stocking metric: a stand's current density expressed as a ratio of its biological maximum (RD = SDI / SDI_max). Runs 0 (open) to ~1 (overstocked) — not a tree count.",
  },
  whp: {
    full: 'Wildfire Hazard Potential',
    gloss:
      'A USFS index of where wildfire would be hardest to suppress if it ignited. Maps fuel and burn-probability conditions only — not weather, ignitions, or values at risk, so it is hazard, not risk.',
    href: 'https://research.fs.usda.gov/firelab/products/dataandtools/wildfire-hazard-potential',
  },
  mobi: {
    full: 'Map of Biodiversity Importance',
    gloss:
      'A NatureServe map of the lower-48 highlighting where habitat for at-risk species (federally listed plus G1 / G2 imperiled species) is concentrated, especially outside existing protected areas.',
    href: 'https://www.natureserve.org/access-data',
  },
  fia: {
    full: 'Forest Inventory and Analysis',
    gloss:
      "The USDA Forest Service's national forest census. Field crews remeasure a permanent grid of plots across all U.S. ownerships, producing the authoritative public inventory of forest area, volume, and growth.",
    href: 'https://research.fs.usda.gov/programs/fia',
  },
  dbh: {
    full: 'Diameter at Breast Height',
    gloss:
      "A tree's stem diameter measured at a standard height — 4.5 ft (1.37 m) above ground in the U.S. The basis of nearly every forester estimate of volume, biomass, and growth.",
  },
  mg_ha: {
    full: 'Megagrams per Hectare',
    gloss:
      'The metric unit for biomass density: one megagram (1,000 kg, ≈ 1 metric ton) per hectare (2.47 acres). 1 Mg/ha ≈ 0.45 short tons per acre.',
  },
  shannon_index: {
    full: 'Shannon Diversity Index',
    gloss:
      "A single-number summary of community diversity: H' = -Σ pᵢ ln pᵢ, where pᵢ is each species' share. Higher values mean more species and more even abundance; real forest stands typically fall around 1.5–3.5.",
    href: 'https://en.wikipedia.org/wiki/Diversity_index#Shannon_index',
  },
  pielou_evenness: {
    full: "Pielou's Evenness (J)",
    gloss:
      "A 0–1 index of how evenly individuals are split across species, defined as J = H' / ln(S), where H' is Shannon and S is species count. 1 means perfectly even; near 0 means one species dominates.",
    href: 'https://www.statology.org/how-to-calculate-interpret-pielous-evenness-index/',
  },
  g1_g2: {
    full: 'NatureServe G1 / G2 Ranks',
    gloss:
      'Global imperilment ranks. G1 = Critically Imperiled (typically ≤5 occurrences or ≤1,000 individuals globally); G2 = Imperiled (typically 6–20 occurrences or 1,001–3,000 individuals). Both at heightened risk of extinction.',
    href: 'https://www.natureserve.org/nsexplorer/about-the-data/statuses/conservation-status-categories',
  },
  luca: {
    full: 'Land Use Change Alerts',
    gloss:
      "CTrees' near-real-time satellite alerts flagging recent tree-cover loss, with a small minimum mapping unit and a bi-weekly cadence. Alerts are leading indicators that need verification before being attributed as deforestation.",
    href: 'https://ctrees.org/luca',
  },
  nsvb: {
    full: 'National Scale Volume and Biomass',
    gloss:
      "The USDA Forest Service's modeling system that estimates each tree's wood volume, biomass, and carbon from species and DBH using one consistent framework across the whole U.S. Released in FIADB in 2023.",
    href: 'https://research.fs.usda.gov/programs/fia/nsvb',
  },
  treemap: {
    full: 'TreeMap',
    gloss:
      'A USFS 30 m raster (conterminous U.S.) that imputes the most likely FIA plot to every pixel, surfacing species composition, stand age, basal area, and forest type. Used here to stratify each crown into the right NSVB allometric class.',
    href: 'https://research.fs.usda.gov/rmrs/products/dataandtools/tools/treemap',
  },
  pwrsr: {
    full: 'Protection-Weighted Range-Size Rarity',
    gloss:
      "A NatureServe metric that gives extra weight to species with small ranges that fall outside protected areas. High values flag places where unprotected habitat carries an outsized share of an imperiled species' total range.",
  },
  sawtimber: {
    full: 'Sawtimber',
    gloss:
      'A live tree large enough to be cut for lumber. FIA defines the minimum DBH as 9" for softwoods and 11" for hardwoods; below those thresholds a tree is poletimber or seedling/sapling.',
    href: 'https://www.nrs.fs.usda.gov/fia/data-tools/state-reports/glossary/default.asp',
  },
  fia_forest_type: {
    full: 'FIA Forest Type',
    gloss:
      "FIA's classification of a stand by its dominant tree species — assigned algorithmically based on which species hold the plurality of live-tree stocking. Stored as the FORTYPCD code in the FIA database.",
    href: 'https://research.fs.usda.gov/programs/fia',
  },
};

export const isGlossaryTermId = (raw: string): raw is GlossaryTermId =>
  raw in GLOSSARY;
